import React, { useState, useEffect, useMemo } from 'react';
import { Link, NavLink, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import useHrPermissions from '../../hooks/useHrPermissions';
import { HR_GROUPS, findHRItemForPath, visibleHRGroupItems, getVisibleHRGroup, canSeeHRRoute, canEnterHRPortal } from '../../config/hrPageGroups';
import { HRAccessProvider } from './HRAccessContext';
import { MaskProvider, useMask } from '../../context/MaskContext';
import HRGroupTabs from './HRGroupTabs';
import IdeaWidget from '../../components/IdeaWidget'; // V70.70 — Fikrim Var portalda da
import WorkTypeSwitch from '../../components/WorkTypeSwitch'; // V71.3.2 — çalışma şekli + portalda Hub açık izi
import './HRApp.css';

// V70.182 — sidebar bölümü: başlık (varsa) + grup satırları. Üç bölüm de aynı kalıbı kullanır.
const renderSection = (groups, heading, activeGroupKey) => {
  if (!groups.length) return null;
  return (
    <>
      {heading && <div className="hr-app-nav-group">{heading}</div>}
      {groups.map(g => (
        <NavLink key={g.key} to={g.visible[0].to}
          className={() => 'hr-app-nav-link' + (activeGroupKey === g.key ? ' active' : '')}>
          <span className="hr-app-nav-icon hr-nav-chip"
            style={{ background: `linear-gradient(135deg, ${g.color[0]}, ${g.color[1]})` }}>{g.emoji}</span>
          <span>{g.label}</span>
        </NavLink>
      ))}
    </>
  );
};

// V71 — veri gizleme (maaş/bordro tutarları, personel adları): hub TopBar'daki düğmenin portal karşılığı
const HRMaskButton = () => {
  const { masked, toggleMask } = useMask();
  const label = masked ? 'Verileri Göster' : 'Verileri Gizle';
  return (
    <button onClick={toggleMask} className="hr-app-icon-btn" title={label} aria-label={label} aria-pressed={masked}>
      {masked ? '🙈' : '👁️'}
    </button>
  );
};

// V71 — portal modalları ESC ile kapanır (hub AppLayout kalıbı): en üstteki görünür overlay'e tıklanır
const HR_ESC_OVERLAYS = '.hs-modal-bg, .he-modal-bg, .modal-overlay';
// V71 — yönlendirme state'i sabit nesne: <Navigate> state değişince yeniden yönlendirir (sonsuz döngü olmasın)
const DENIED_STATE = Object.freeze({ denied: 'hr' });

export default function HRLayout({ children }) {
  const { user, logout, hasPermission, isAdmin } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const perms = useHrPermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname, search } = location;
  const [mobileOpen, setMobileOpen] = useState(false);

  // V71 — yeni yetki motoru (rol/set/vekalet/istisna) menü ve rota kararına katılır
  const engine = useMemo(() => ({ ready: perms.ready, canExplicit: perms.canExplicit }), [perms.ready, perms.canExplicit]);

  // V70.67 — sidebar gruplara indi (ana uygulamadaki desen): grup başına tek satır,
  // grubun sayfaları üstteki renkli yatay sekme şeridinden (HRGroupTabs) gezilir.
  // V70.182 — spec §3.2: üç bölüm. Operasyonel ekranlar kendi modüllerinde kalır;
  // tanım/politika/limit/kural/sistem ayarı ekranları tek "Yönetim" başlığı altında toplanır.
  const bySection = (kind) => HR_GROUPS.filter(g => g.kind === kind)
    .map(g => ({ ...g, visible: visibleHRGroupItems(g, hasPermission, isAdmin, engine) }))
    .filter(g => g.visible.length > 0);

  const selfGroups = bySection('self');
  const operateGroups = bySection('operate');
  const manageGroups = bySection('manage');
  const canManage = manageGroups.length > 0;

  // Portala giriş (V70.55 semantiği): Modül genel/alt ya da Yönetim tarafından en az bir sayfa
  // V71 — karar hrPageGroups.canEnterHRPortal'da (hub CTA ile ortak)
  const portalAccess = canEnterHRPortal(hasPermission, isAdmin, engine);

  // V71 — şerit + .pg-grouped + aktif grup tek karardan (hrPageGroups.getVisibleHRGroup)
  const view = getVisibleHRGroup(pathname, hasPermission, isAdmin, engine);
  const activeGroupKey = view?.group.key || null;
  const activeItem = findHRItemForPath(pathname);
  const groupStyle = view ? { '--pg-from': view.group.color[0], '--pg-to': view.group.color[1] } : undefined;

  const access = useMemo(() => ({
    engine,
    canSeePath: (p) => canSeeHRRoute(p, hasPermission, isAdmin, engine),
  }), [engine, hasPermission, isAdmin]);
  const routeOk = access.canSeePath(pathname);

  // V71 — yetkisiz portal sayfasından /HR/dashboard'a yönlendirilen kullanıcıya bir kez bildirim
  const denied = location.state?.denied;
  useEffect(() => {
    if (!denied || !/^\/HR(\/|$)/.test(pathname)) return; // hub'daki bildirimi AppLayout gösterir
    toast.warn('Bu sayfaya erişim yetkiniz yok', { toastId: 'hr-route-denied' });
    navigate(`${pathname}${search}`, { replace: true, state: null });
  }, [denied, navigate, pathname, search]);

  // V71 — mobil menü: sayfa değişince kapanır
  useEffect(() => { setMobileOpen(false); }, [pathname]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (document.querySelector('.custom-dialog-overlay')) return;
      const overlays = [...document.querySelectorAll(HR_ESC_OVERLAYS)].filter(el => el.getClientRects().length);
      const overlay = overlays[overlays.length - 1];
      if (overlay) { overlay.click(); return; }
      setMobileOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const initials = (user?.full_name || user?.username || '?')
    .split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();

  // V71 — rol etiketi portaldaki gerçek erişimden türer
  const roleLabel = isAdmin ? 'Yönetici' : canManage ? 'İK Yönetimi' : operateGroups.length ? 'İK Operasyon' : 'Personel';

  // V70.55 — İK yetkisi hiç olmayan kullanıcı portala giremez.
  // V71 — legacy anahtarı yoksa motor (rol/vekalet) sonucu beklenir; yine yoksa hub + bildirim.
  if (!portalAccess) {
    if (perms.loading) return <div className="hr-app-boot">Yükleniyor…</div>;
    return <Navigate to="/" replace state={DENIED_STATE} />;
  }

  // V71 — portal içi rota koruması: menüde gizli sayfa URL ile açılmaz
  let content = children;
  if (!routeOk) {
    content = perms.loading
      ? <div className="hr-app-loading">Yükleniyor…</div>
      : <Navigate to="/HR/dashboard" replace state={DENIED_STATE} />;
  }

  return (
    <HRAccessProvider value={access}>
      <MaskProvider>
      <div className="hr-app">
        <aside className={`hr-app-sidebar${mobileOpen ? ' open' : ''}`}>
          <div className="hr-app-brand">
            <Link to="/HR/dashboard" className="hr-app-brand-link">
              <div className="hr-app-brand-mark">İK</div>
              <div className="hr-app-brand-text">
                <div className="hr-app-brand-title">İnsan Kaynakları</div>
                <div className="hr-app-brand-sub">MTU Travel</div>
              </div>
            </Link>
            <button type="button" className="hr-app-drawer-close" onClick={() => setMobileOpen(false)} aria-label="Menüyü kapat">×</button>
          </div>
          <nav className="hr-app-nav">
            {renderSection(selfGroups, null, activeGroupKey)}
            {renderSection(operateGroups, 'Operasyon', activeGroupKey)}
            {renderSection(manageGroups, 'Yönetim', activeGroupKey)}
          </nav>
          <div className="hr-app-foot">
            <IdeaWidget />
            <Link to="/" className="hr-app-back">← Hub'a dön</Link>
          </div>
        </aside>
        {mobileOpen && <div className="hr-app-backdrop" onClick={() => setMobileOpen(false)} />}
        <div className="hr-app-main">
          <header className="hr-app-topbar">
            <button type="button" className="hr-app-menu-btn" onClick={() => setMobileOpen(true)} aria-label="Menüyü aç">☰</button>
            {/* V71 — kırıntı: portal › grup › sayfa (şeridin olmadığı tek sayfalı gruplarda da konum belli) */}
            <nav className="hr-app-topbar-title" aria-label="Konum">
              <span className="hr-app-crumb-root">İnsan Kaynakları Portalı</span>
              {view && <><span className="hr-app-crumb-sep">›</span><span className="hr-app-crumb">{view.group.label}</span></>}
              {activeItem && view && activeItem.label !== view.group.label && (
                <><span className="hr-app-crumb-sep">›</span><span className="hr-app-crumb cur">{activeItem.label}</span></>
              )}
            </nav>
            <div className="hr-app-topbar-actions">
              <WorkTypeSwitch />
              <HRMaskButton />
              <button onClick={toggleTheme} className="hr-app-icon-btn" title={theme === 'dark' ? 'Light moda geç' : 'Dark moda geç'}>
                {theme === 'dark' ? '☀️' : '🌙'}
              </button>
              <div className="hr-app-user">
                <div className="hr-app-avatar">{initials}</div>
                <div className="hr-app-user-meta">
                  <div className="hr-app-user-name">{user?.full_name || user?.username}</div>
                  <div className="hr-app-user-role">{roleLabel}</div>
                </div>
                <button className="hr-app-logout" onClick={() => { logout(); navigate('/login'); }}>Çıkış</button>
              </div>
            </div>
          </header>
          <HRGroupTabs view={view} />
          <main className={`hr-app-content${view?.showTabs ? ' pg-grouped' : ''}`} style={groupStyle}>{content}</main>
        </div>
      </div>
      </MaskProvider>
    </HRAccessProvider>
  );
}
